import React from 'react';
import styled, { keyframes } from 'styled-components';

/**
 * Original ↔ Dubbed ↔ Both audio mode (PLAT-10597 DE6). `both` plays the
 * original and the dub together (the hear-both overlay, PLAT-10600).
 */
export type AudioMode = 'original' | 'dubbed' | 'both';

const OPTIONS: { value: AudioMode; label: string; hint: string }[] = [
  { value: 'original', label: 'Original', hint: 'Play the original recording' },
  { value: 'dubbed', label: 'Dubbed', hint: 'Play the AI dub' },
  {
    value: 'both',
    label: 'Both',
    hint: 'Play the original and the dubbed voice together',
  },
];

interface AudioModeSegmentedToggleProps {
  mode: AudioMode;
  onChange: (mode: AudioMode) => void;
  /** Small leading label. Defaults to "Voice"; pass `null` to hide it. */
  label?: string | null;
  ariaLabel?: string;
  className?: string;
  /**
   * react-tooltip id to anchor a hover hint on each segment ("Both" is the one
   * nobody can guess — PLAT-11563). The host renders the <Tooltip> for this id
   * so it can pick the style and z-index; omitted ⇒ no hints.
   */
  tooltipId?: string;
  /**
   * A run started from this control is still under way. While set the segments
   * are unpressable and the strip paints as working, so nobody starts a second
   * run over the first.
   */
  busy?: boolean;
}

/**
 * The compact Original | Dubbed | Both segmented control. Shared by the
 * Transcript & Speakers language dropdown header (PLAT-10597) and the
 * Translation Review quick toggle (PLAT-10600) so both surfaces drive the same
 * control from one source of truth. Gold (`--accent`) active state — a
 * voice/dub selector is a visual/creative control (design-system.md).
 */
export const AudioModeSegmentedToggle: React.FC<
  AudioModeSegmentedToggleProps
> = ({
  mode,
  onChange,
  label = 'Voice',
  ariaLabel = 'Audio: original or dubbed voice',
  className,
  tooltipId,
  busy = false,
}) => (
  <Wrapper
    role="group"
    aria-label={ariaLabel}
    className={className}
    // Left off entirely when nothing is passed, so a surface that does not use
    // the busy state renders the markup it always did.
    aria-busy={busy || undefined}
    $busy={busy}
  >
    {label !== null && <span className="label">{label}</span>}
    <div className="segments">
      {OPTIONS.map(opt => (
        <button
          key={opt.value}
          type="button"
          className={mode === opt.value ? 'active' : ''}
          aria-pressed={mode === opt.value}
          disabled={busy || undefined}
          data-tooltip-id={tooltipId}
          data-tooltip-content={tooltipId ? opt.hint : undefined}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  </Wrapper>
);

const working = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;

const Wrapper = styled.div<{ $busy: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 8px 8px;
  margin-bottom: 4px;
  border-bottom: 1px solid var(--border);

  .label {
    font-size: var(--text-xs);
    font-weight: var(--font-weight-semibold);
    color: var(--text-muted);
  }
  .segments {
    display: inline-flex;
    background: var(--surface-elevated);
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 2px;
    gap: 2px;
    opacity: ${({ $busy }) => ($busy ? 0.75 : 1)};
  }
  .segments button {
    border: 0;
    background: transparent;
    padding: 3px 10px;
    border-radius: 4px;
    font-size: var(--text-xs);
    font-weight: var(--font-weight-medium);
    color: var(--text-secondary);
    cursor: ${({ $busy }) => ($busy ? 'progress' : 'pointer')};
    transition:
      background-color 120ms ease,
      color 120ms ease;
  }
  .segments button:hover:not(.active) {
    color: var(--foreground);
  }
  .segments button.active {
    /* Gold (--accent): a voice/dub selector is a visual/creative control, and
       this matches the waveform header voice label (design-system.md). */
    background: var(--accent);
    color: var(--accent-foreground, #fff);
    animation: ${({ $busy }) => ($busy ? working : 'none')} 1.1s ease-in-out
      infinite;
  }
`;
